/**
 * Sheet-scan image preparation — downscale, type-check and base64-encode the
 * photos a worker snaps of a printed production-record sheet.
 *
 * `DOWNSCALE_MAX_DIMENSION`, `targetDimensions` and `downscaleForUpload` MOVED
 * here from `lib/chefs/temp-log-photos.ts` (PROD-1279 plan 03 §D.1) so the chef
 * temp-log page and the two school tasks share one pipeline; that module keeps
 * a re-export line, so nothing that imported them from there had to change.
 *
 * Two deliberate choices:
 *
 * 1. `createImageBitmap(file, { imageOrientation: 'from-image' })`. A phone
 *    photo carries its rotation in EXIF, and the vision API strips image
 *    metadata before the model sees the pixels, so a photo that reaches the
 *    wire with its rotation still in the header is a SIDEWAYS sheet: the
 *    model reads real digits out of the wrong columns, and nothing errors.
 *    Two consequences, both pinned in `images.test.ts`:
 *      - an engine that predates the `from-image` member (WebKit before 16,
 *        Chrome before 112) throws a TypeError on the option rather than
 *        ignoring it, so `decodeBitmap` retries without options — those
 *        engines apply EXIF by default — instead of falling through to the
 *        original file;
 *      - once a decode succeeded the RE-ENCODED JPEG is always what comes
 *        back, even when it is no smaller than the input. The old "re-encoding
 *        didn't help" shortcut handed a small, already-efficient JPEG back
 *        untouched — rotation and all.
 * 2. `fileToImagePayload` uses `arrayBuffer()` + chunked `btoa`, never
 *    `FileReader`. `FileReader` is undefined under `bun test` (verified), which
 *    is what makes the chef's equivalent (`lib/chefs/temp-log.ts`) structurally
 *    untestable. The chunking is not optional: one
 *    `String.fromCharCode(...bytes)` over a 6MB buffer blows the argument limit.
 */

// ---------------------------------------------------------------------------
// Downscale — an 8MB phone photo becomes ~500KB before it is stored / sent for
// extraction. Claude vision downsamples past ~1568px anyway, so nothing is lost
// at 2000px.
// ---------------------------------------------------------------------------

export const DOWNSCALE_MAX_DIMENSION = 2000
const DOWNSCALE_QUALITY = 0.82

/**
 * Fit `width`×`height` inside `maxDim` preserving aspect ratio; images
 * already small enough come back unchanged. Pure for unit tests.
 */
export function targetDimensions(
  width: number,
  height: number,
  maxDim: number = DOWNSCALE_MAX_DIMENSION,
): { width: number; height: number } {
  const longest = Math.max(width, height)
  if (longest <= maxDim || longest <= 0) {
    return { width, height }
  }
  const scale = maxDim / longest
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) }
}

/**
 * Decode with EXIF orientation applied. The `from-image` member of
 * `imageOrientation` is a TypeError on an engine that predates it (an unknown
 * enum value is rejected, not ignored), and those engines honour EXIF by
 * default — so the retry without options is the same decode, not a worse one.
 */
async function decodeBitmap(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch (err) {
    if (err instanceof TypeError) {
      return await createImageBitmap(file)
    }
    throw err
  }
}

/**
 * Re-encode a sheet photo as a ≤`maxDim`px JPEG, honouring EXIF orientation.
 * Best-effort: a decode/encode failure (exotic format, canvas unavailable)
 * returns the original file so the flow never breaks on the optimization. A
 * decode that SUCCEEDED always yields the re-encoded file, whatever its size:
 * the re-encode is what carries the orientation into the pixels.
 */
export async function downscaleForUpload(file: File, maxDim: number = DOWNSCALE_MAX_DIMENSION): Promise<File> {
  try {
    const bitmap = await decodeBitmap(file)
    try {
      const { width, height } = targetDimensions(bitmap.width, bitmap.height, maxDim)
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        return file
      }
      ctx.drawImage(bitmap, 0, 0, width, height)
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', DOWNSCALE_QUALITY))
      if (!blob) {
        return file
      }
      const jpegName = file.name.replace(/\.[^.]+$/, '') + '.jpg'
      return new File([blob], jpegName, { type: 'image/jpeg' })
    } finally {
      bitmap.close()
    }
  } catch {
    return file
  }
}

// ---------------------------------------------------------------------------
// Encode for the scan endpoint
// ---------------------------------------------------------------------------

/**
 * What the server's body validator accepts. HEIC is absent on purpose: Safari
 * normally decodes it through `createImageBitmap`, so `downscaleForUpload`
 * hands back a JPEG; when it cannot, the raw HEIC lands here and produces a
 * per-photo error instead of a mystery 400.
 */
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const

/** A prepared photo larger than this is refused rather than sent. */
export const MAX_PREPARED_BYTES = 6 * 1024 * 1024

/** Chunk size for the base64 conversion — small enough to stay under the spread-argument limit. */
const BASE64_CHUNK_BYTES = 8 * 1024

export interface ScanImagePayload {
  mediaType: string
  dataBase64: string
}

export interface PreparedImages {
  payloads: ScanImagePayload[]
  /**
   * Device position of each SENT photo, parallel to `payloads`. The server
   * indexes its `images[]` over what it received, so with a rejected photo in
   * the middle those indices no longer line up with the worker's own page
   * numbers — `remapPhotoIndices` puts them back before any warning is phrased.
   */
  sourceIndices: number[]
  /** One user-readable line per REJECTED photo; the good ones still go. */
  errors: string[]
}

/** File bytes to raw base64. Uses `arrayBuffer` + chunked `btoa`, NOT `FileReader`. */
export async function fileToImagePayload(file: File): Promise<ScanImagePayload> {
  const bytes = new Uint8Array(await file.arrayBuffer())
  let binary = ''
  for (let offset = 0; offset < bytes.length; offset += BASE64_CHUNK_BYTES) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + BASE64_CHUNK_BYTES))
  }
  return { mediaType: file.type, dataBase64: btoa(binary) }
}

/**
 * Downscale, type-check and encode each photo, in order. One bad photo never
 * fails the batch — it contributes an error line and the rest still go (D25).
 *
 * Every photo keeps its DEVICE position: the error lines are numbered by it,
 * and `sourceIndices` carries it for the ones that are actually sent, so a
 * rejected page in the middle cannot shift what the server's later warnings
 * call "Photo N".
 *
 * Sequential on purpose: decoding six 8MB photos in parallel on a phone is how
 * a cafeteria tablet runs out of memory mid-task.
 */
export async function filesToImagePayloads(files: File[]): Promise<PreparedImages> {
  const payloads: ScanImagePayload[] = []
  const sourceIndices: number[] = []
  const errors: string[] = []
  for (let i = 0; i < files.length; i += 1) {
    const file = files[i]
    if (!file) {
      continue
    }
    const label = i + 1
    const prepared = await downscaleForUpload(file)
    if (!(ACCEPTED_IMAGE_TYPES as readonly string[]).includes(prepared.type)) {
      errors.push(`Photo ${label} is not a supported image. Use a JPEG, PNG, or WebP photo.`)
      continue
    }
    if (prepared.size > MAX_PREPARED_BYTES) {
      errors.push(`Photo ${label} is too large to send. Try taking it again.`)
      continue
    }
    payloads.push(await fileToImagePayload(prepared))
    sourceIndices.push(i)
  }
  return { payloads, sourceIndices, errors }
}
